import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { isFuture } from 'date-fns'
import { sortBy } from 'lodash-es'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { getClientHasActiveAiConversation } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/utils'
import { CalendarAppointmentsView } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import {
  ConversationFilter,
  ConversationTag,
} from '@lyrahealth-inc/ui-core-crossplatform/src/ui-models/messages/Conversations'

import { getClientFullName } from '../../../common/utils/utils'
import { useClientRiskFlags } from '../../../dashboard/openItems/hooks/useClientRiskFlags'
import { getAuthConfig, getAuthUserId } from '../../../data/auth/authSelectors'
import { useIsEngageDAProvider } from '../../../hooks/useIsEngageDAProvider'
import { useGetAppointmentsQuery } from '../../clients/clientDetails/data/appointmentsApi'
import { useAllClientsData } from '../../clients/data/hooks/useAllClientsData'
import { useClientsInProgramData } from '../../clients/data/hooks/useClientsInProgramData'
import { useConversationsData } from '../data/hooks/useConversationsData'
import { useProviderPairInboxConversations } from '../data/hooks/useProviderPairConversations'
import { ClientInboxSummary, summarizeClientInbox } from '../data/inboxSummaries'

type ClientListClientObjectWithExtraFields = ClientListClientObject & { active: boolean; unscheduled: boolean }

const filterByTag = (
  client: ClientListClientObjectWithExtraFields,
  filters: ConversationFilter[],
  summary: ClientInboxSummary | undefined,
  hasActiveAiCoachConversation: boolean,
) => {
  if (filters.includes(ConversationFilter.ACTIVE)) {
    if (!client.active) {
      return false
    }
  }
  if (filters.includes(ConversationFilter.UNREAD)) {
    if (!summary?.unreadCount) {
      return false
    }
  }

  if (filters.includes(ConversationFilter.UNSCHEDULED)) {
    if (!client.active || !client.unscheduled) {
      return false
    }
  }

  if (filters.includes(ConversationFilter.LYRA_AI_ENABLED)) {
    if (!hasActiveAiCoachConversation) {
      return false
    }
  }

  return true
}

const useGetFilteredConversations = (filters: ConversationFilter[]) => {
  const userId = useSelector(getAuthUserId)
  const { isPreferredNameEnabled, isDBTTeensEnabled, isProviderProviderMessagingEnabled } = useFlags()
  const { data: conversations, isLoading: isLoadingConversations } = useConversationsData()
  const { currentData: providerPairConversations } = useProviderPairInboxConversations()

  const { data: aiCoachConversations, isLoading: isLoadingAiCoachConversations } = useConversationsData({
    types: CONVERSATION_TYPE.AI_COACH,
  })

  const riskFlagClientIds = useClientRiskFlags()
  const isDAProvider = useIsEngageDAProvider()
  const [
    _,
    { data: activeClients, isLoading: isLoadingActiveClients },
    { data: inactiveClients, isLoading: isLoadingInactiveClients },
  ] = useAllClientsData()
  const openClientAccessSupported = useSelector(getAuthConfig)?.openClientAccessSupported ?? false
  const shouldFetchDbtTeensClients = openClientAccessSupported && isDBTTeensEnabled
  const { data: dbtTeensClients, isLoading: isLoadingDbtTeensClients } = useClientsInProgramData(
    { programName: ProgramNames.DialecticalBehaviorTherapyTeens, interactionMode: 'all' },
    { skip: !shouldFetchDbtTeensClients },
  )
  const { data: appointments, isLoading: isLoadingAppointments } = useGetAppointmentsQuery({
    providerId: userId,
    durationRange: { after: { days: 90 } },
    view: CalendarAppointmentsView.CALENDAR,
  })
  const futureAppointments = useMemo(
    () =>
      appointments?.filter((appointment) => {
        const sessionStart = getDateFromAppointment(appointment)
        return isFuture(sessionStart) && appointment.appointmentStatus !== 'canceled'
      }),
    [appointments],
  )

  const allClients = useMemo(() => {
    const providerClients = [
      ...(activeClients?.map((client) => ({
        ...client,
        active: true,
      })) ?? []),
      ...(inactiveClients?.map((client) => ({
        ...client,
        active: false,
      })) ?? []),
    ]
    const providerClientIds = new Set(providerClients.map((c) => c.id))
    const openAccessClients =
      dbtTeensClients
        ?.filter((client) => !providerClientIds.has(client.id))
        .map((client) => ({ ...client, active: true })) ?? []

    return [...providerClients, ...openAccessClients].map((client) => ({
      ...client,
      unscheduled: !futureAppointments?.some((appointment) => appointment.userInfo?.lyraId === client.id),
    }))
  }, [activeClients, dbtTeensClients, futureAppointments, inactiveClients])

  const inboxSummaries = useMemo(
    () =>
      summarizeClientInbox(
        conversations ?? [],
        isProviderProviderMessagingEnabled ? (providerPairConversations ?? []) : [],
      ),
    [conversations, isProviderProviderMessagingEnabled, providerPairConversations],
  )

  const mappedConversations = useMemo(() => {
    if (!conversations) {
      return []
    }
    return allClients.flatMap((client) => {
      const summary = inboxSummaries.get(client.id)
      const hasActiveAiCoachConversation = getClientHasActiveAiConversation(client.id, aiCoachConversations)
      if (!filterByTag(client, filters, summary, hasActiveAiCoachConversation)) {
        return []
      }
      const tags = []
      if (isDAProvider && riskFlagClientIds.has(client.id)) {
        tags.push(ConversationTag.AT_RISK)
      }
      if (client.active) {
        client.unscheduled && tags.push(ConversationTag.UNSCHEDULED)
        hasActiveAiCoachConversation && tags.push(ConversationTag.AI_COACH_ENABLED)
      } else {
        tags.push(ConversationTag.INACTIVE)
      }
      return [
        {
          clientId: client.id,
          clientName: client ? getClientFullName(client, isPreferredNameEnabled) : '',
          lastMessageTime: summary?.lastMessageTime ?? null,
          hasUnreadMessage: (summary?.unreadCount ?? 0) > 0,
          threadCount:
            isProviderProviderMessagingEnabled && summary?.providerPairThreadCount ? summary.threadCount : undefined,
          tags,
        },
      ]
    })
  }, [
    aiCoachConversations,
    allClients,
    conversations,
    filters,
    inboxSummaries,
    isPreferredNameEnabled,
    isProviderProviderMessagingEnabled,
    isDAProvider,
    riskFlagClientIds,
  ])

  const sortedConversations = useMemo(
    () =>
      sortBy(mappedConversations, (conversation) =>
        conversation.lastMessageTime ? conversation.lastMessageTime.getTime() * -1 : Number.MIN_VALUE,
      ),
    [mappedConversations],
  )

  return {
    conversations: sortedConversations,
    isLoading:
      isLoadingConversations ||
      isLoadingAiCoachConversations ||
      isLoadingAppointments ||
      isLoadingActiveClients ||
      isLoadingInactiveClients ||
      isLoadingDbtTeensClients,
  }
}

export default useGetFilteredConversations
